import { computed, DestroyRef, ElementRef, EventEmitter, inject, input, Renderer2, signal } from '@angular/core';
import { UiDragDropModule } from '@common/ui/frontend-shared/dragdrop';
import { UiIconComponent } from '@common/ui/frontend-shared/icons';
import { takeWhileAlive } from '@core/frontend-shared';
import { filter, fromEvent, merge, throttleTime } from 'rxjs';
import { MediaAssetTimelineTrackItemComponent } from './media-asset-timeline-track-item.component';
import { TimelineViewChanged } from './timeline-events';
import { TimelineService } from './timeline.service';
import * as i0 from "@angular/core";
const _c0 = ["addItemButton"];
function _forTrack0($index, $item) {
  return this.trackBy($item);
}
function MediaAssetTimelineTrackComponent_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "media-asset-timeline-track-item", 2);
  }
  if (rf & 2) {
    const item_r1 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("timelineLength", ctx_r1.length())("item", item_r1);
  }
}
export let MediaAssetTimelineTrackComponent = /*#__PURE__*/(() => {
  class MediaAssetTimelineTrackComponent {
    constructor() {
      this.destroyRef = inject(DestroyRef);
      this.timeline = inject(TimelineService);
      this.el = inject(ElementRef);
      this.renderer = inject(Renderer2);
      this.trackId = input.required(...(ngDevMode ? [{
        debugName: "trackId"
      }] : /* istanbul ignore next */[]));
      this.items = input([], ...(ngDevMode ? [{
        debugName: "items"
      }] : /* istanbul ignore next */[]));
      this.selection = input(null, ...(ngDevMode ? [{
        debugName: "selection"
      }] : /* istanbul ignore next */[]));
      this.processedItems = computed(() => {
        const selection = this.selection();
        const items = this.items().map(i => {
          return {
            ...i,
            startConnected: false,
            endConnected: false,
            selected: selection === i.id
          };
        });
        for (let i = 0; i < items.length; i++) {
          const curr = items[i];
          const next = items[i + 1];
          if (next) {
            if (curr.end === next.start) {
              curr.endConnected = true;
              next.startConnected = true;
            }
          }
        }
        return items;
      }, ...(ngDevMode ? [{
        debugName: "processedItems"
      }] : /* istanbul ignore next */[]));
      this.length = input.required(...(ngDevMode ? [{
        debugName: "length"
      }] : /* istanbul ignore next */[]));
      this.showAddItem = signal(false, ...(ngDevMode ? [{
        debugName: "showAddItem"
      }] : /* istanbul ignore next */[]));
      this.addItemPosSec = signal(0, ...(ngDevMode ? [{
        debugName: "addItemPosSec"
      }] : /* istanbul ignore next */[]));
      this.addItemAt = new EventEmitter();
    }
    trackBy(item) {
      return item.id;
    }
    ngOnInit() {
      merge(fromEvent(this.el.nativeElement, 'mouseover'), fromEvent(this.el.nativeElement, 'mouseout')).pipe(
      // throttleTime(20),
      takeWhileAlive(this.destroyRef), filter(() => !this.timeline.isDragging()),
      // disable events during dragging!
      filter(e => e.target === this.el.nativeElement)).subscribe(e => {
        this.showAddItem.set(e.type === 'mouseover');
      });
      fromEvent(this.el.nativeElement, 'mousemove').pipe(throttleTime(20), takeWhileAlive(this.destroyRef)).subscribe(e => {
        //if not matching, we're hovering a track item
        if (e.target === this.el.nativeElement) {
          const pos = e.offsetX;
          this.addItemPosSec.set(this.timeline.pxPositionToTimelineSeconds(pos + this.timeline.timelinePadding()).timelineSeconds);
          this.renderer.setStyle(this.addItemButton.nativeElement, 'left', pos + 'px');
        }
      });
      this.timeline.events$.pipe(takeWhileAlive(this.destroyRef), filter(e => e instanceof TimelineViewChanged)).subscribe(() => {
        if (!this.addItemButton?.nativeElement) return;
        const lastPositionSec = this.addItemPosSec();
        const posInfo = this.timeline.timelineSecondsToPxPosition(lastPositionSec);
        this.renderer.setStyle(this.addItemButton.nativeElement, 'left', posInfo.offsetX - this.timeline.timelinePadding() + 'px');
      });
      fromEvent(this.el.nativeElement, 'click').pipe(takeWhileAlive(this.destroyRef)).subscribe(e => {
        if (e.target === this.el.nativeElement) {
          const seconds = this.timeline.pxPositionToTimelineSeconds(e.offsetX + this.timeline.timelinePadding()).timelineSeconds;
          this.addItemAt.emit({
            seconds
          });
        }
      });
    }
    static {
      this.ɵfac = function MediaAssetTimelineTrackComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MediaAssetTimelineTrackComponent)();
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: MediaAssetTimelineTrackComponent,
        selectors: [["media-asset-timeline-track"]],
        viewQuery: function MediaAssetTimelineTrackComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.addItemButton = _t.first);
          }
        },
        hostVars: 2,
        hostBindings: function MediaAssetTimelineTrackComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("show-add-item", ctx.showAddItem());
          }
        },
        inputs: {
          trackId: [1, "trackId"],
          items: [1, "items"],
          selection: [1, "selection"],
          length: [1, "length"]
        },
        outputs: {
          addItemAt: "addItemAt"
        },
        decls: 8,
        vars: 0,
        consts: [["addItemButton", ""], [1, "track"], [3, "timelineLength", "item"], [1, "add-item"], ["icon", "plus"]],
        template: function MediaAssetTimelineTrackComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 1);
            i0.ɵɵrepeaterCreate(1, MediaAssetTimelineTrackComponent_For_2_Template, 1, 2, "media-asset-timeline-track-item", 2, _forTrack0, true);
            i0.ɵɵelementStart(3, "button", 3, 0);
            i0.ɵɵelement(5, "ui-icon", 4);
            i0.ɵɵelementStart(6, "small");
            i0.ɵɵtext(7, "neu");
            i0.ɵɵelementEnd()()();
          }
          if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵrepeater(ctx.processedItems());
          }
        },
        dependencies: [UiDragDropModule, MediaAssetTimelineTrackItemComponent, UiIconComponent],
        styles: ["[_nghost-%COMP%]{height:35px;display:block;transition:background .4s,box-shadow .4s;box-shadow:0 0 #8080801a}[_nghost-%COMP%]:hover{background:#80808012;box-shadow:0 0 10px 4px #8080801a}.add-item[_ngcontent-%COMP%]{transition:background-color .2s,opacity .2s;border:0;background:0;color:var(--textColor)}.add-item[_ngcontent-%COMP%]{opacity:0;position:absolute;padding:0;top:-12px;pointer-events:none;margin-left:-7px}.add-item[_ngcontent-%COMP%]{display:flex;align-items:center}.add-item[_ngcontent-%COMP%]   small[_ngcontent-%COMP%]{margin-left:4px}.show-add-item[_nghost-%COMP%]{cursor:pointer}.show-add-item[_nghost-%COMP%]   .add-item[_ngcontent-%COMP%]{opacity:1}"],
        changeDetection: 0
      });
    }
  }
  return MediaAssetTimelineTrackComponent;
})();